import {
  json,
  type LoaderFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction,
} from '@remix-run/node'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from '@remix-run/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { prisma } from './app/config/db.server.js'
import { getEnv } from './app/config/env.server.js'
import { useNonce } from './app/config/nonce-provider.js'
import fontStylestylesheetUrl from './app/styles/font.css?url'
import tailwindStyleSheetUrl from './app/styles/tailwind.css?url'
import { authenticator, getUserId } from './features/auth/auth.server.js'
import { ClientHintCheck, getHints } from './shared/lib/client-hints.js'
import { honeypot } from './shared/lib/honeypot.server.js'
import { getMetaTags } from './shared/lib/meta-tags.js'
import { combineHeaders, getDomainUrl } from './shared/lib/misc.js'
import { makeTimings, time } from './shared/lib/timing.server.js'
import { LegalModalProvider } from './shared/ui/data-display/LegalModalContext.js'
import { PackOptimaModalProvider } from './shared/ui/data-display/PackOptimaModalContext.js'
import { GeneralErrorBoundary } from './shared/ui/error-boundary.js'
import { ProgressBar } from './shared/ui/feedback/ProgressBar.js'
import { Toast } from './shared/ui/feedback/toast/Toast.js'
import { getToast } from './shared/ui/feedback/toast/toast.server.js'
import { useToast } from './shared/ui/feedback/toast/useToast.js'
import { href as iconsHref } from './shared/ui/icons/Icon.js'

export const links: LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: 'preload', href: iconsHref, as: 'image' },
    { rel: 'preload', href: fontStylestylesheetUrl, as: 'style' },
    { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
    {
      rel: 'icon',
      href: '/favicon.ico',
      sizes: '48x48',
      media: '(prefers-color-scheme: dark)',
    },
    {
      rel: 'icon',
      href: '/iso-go-light.png',
      sizes: '192x192',
      media: '(prefers-color-scheme: light)',
    },
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: '/favicon.svg',
      media: '(prefers-color-scheme: dark)',
    },
    {
      rel: 'apple-touch-icon',
      href: '/apple-touch-icon.png',
      sizes: '180x180',
    },
    { rel: 'stylesheet', href: fontStylestylesheetUrl },
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ]
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return getMetaTags({
    ENV: data?.ENV as any,
  })
}

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader')
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  })

  const user = userId
    ? await time(
        () =>
          prisma.user.findUniqueOrThrow({
            select: {
              id: true,
            },
            where: { id: userId },
          }),
        { timings, type: 'find user', desc: 'find user in root' },
      )
    : null
  if (userId && !user) {
    console.info('something weird happened')
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await await authenticator.logout(request, { redirectTo: `/` })
  }
  const { toast, headers: toastHeaders } = await getToast(request)
  const honeyProps = honeypot.getInputProps()

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
      },
      ENV: getEnv(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
      ),
    },
  )
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
  }
  return headers
}

function Document({
  children,
  nonce,
  env = {},
}: {
  children: React.ReactNode
  nonce: string
  env?: Record<string, string>
}) {
  return (
    <html lang="es" className={`h-full overflow-x-hidden`}>
      <head>
        {env.MODE === 'staging' && (
          <script
            id="Cookiebot"
            src="https://consent.cookiebot.com/uc.js"
            data-cbid="22418cb6-2606-4720-8cb0-83ac5b77153f"
            data-blockingmode="auto"
            type="text/javascript"
          />
        )}
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Links />
        {env.MODE === 'production' && (
          <>
            {/* Google Tag Manager */}
            <script
              dangerouslySetInnerHTML={{
                __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-NHZTVX8M');`,
              }}
            />
          </>
        )}
      </head>
      <body className="bg-background text-foreground">
        {/* Google Tag Manager (noscript) */}
        {env.MODE === 'production' && (
          <noscript>
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=GTM-NHZTVX8M"
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
              title="Google Tag Manager"
            />
          </noscript>
        )}
        {/* End Google Tag Manager (noscript) */}
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  )
}

export function Layout({ children }: { children: React.ReactNode }) {
  // if there was an error running the loader, data could be missing
  const data = useLoaderData<typeof loader | null>()
  const nonce = useNonce()
  return (
    <Document nonce={nonce} env={data?.ENV}>
      {children}
    </Document>
  )
}

function App() {
  const data = useLoaderData<typeof loader>()
  useToast(data.toast)

  return (
    <>
      <Outlet />
      <Toast closeButton position="top-center" />
      <ProgressBar />
    </>
  )
}

export default function AppWithProviders() {
  const data = useLoaderData<typeof loader>()
  return (
    <HoneypotProvider {...data.honeyProps}>
      <PackOptimaModalProvider>
        <LegalModalProvider>
          <App />
        </LegalModalProvider>
      </PackOptimaModalProvider>
    </HoneypotProvider>
  )
}

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
